import { Helmet } from "react-helmet-async";
import { BRAND } from "@/brand";

const SITE_URL = (process.env.REACT_APP_SITE_URL || "https://gcspark.com").replace(/\/$/, "");

/**
 * SEO component — handles per-page meta + multi-schema JSON-LD.
 *
 * Props:
 *   title         page-specific title (will be wrapped in `${title} | GC SPARK`)
 *   description   meta description (150-160 chars ideal)
 *   path          relative path; combined with SITE_URL to form canonical + og:url
 *   image         absolute or relative URL for og:image / twitter:image
 *   type          og:type (default "website", use "article" for blog posts)
 *   noindex       set true to mark page as noindex,nofollow
 *   schema        single JSON-LD object  (legacy support)
 *   schemas       array of JSON-LD objects (preferred)
 *   article       optional { author, publishedAt, modifiedAt, section, tags[] } → emits Article schema
 *   breadcrumb    optional array of { name, path } → emits BreadcrumbList schema
 *   faq           optional array of { q, a } → emits FAQPage schema
 */
export default function SEO({
  title,
  description,
  path,
  canonical,
  image,
  type = "website",
  noindex = false,
  schema,
  schemas,
  article,
  breadcrumb,
  faq,
}) {
  const fullTitle = title ? `${title} | ${BRAND.name}` : `${BRAND.name} — ${BRAND.tagline}`;
  const desc = description || BRAND.description;
  const url = canonical || (path ? `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}` : null);
  const ogImage = image
    ? image.startsWith("http")
      ? image
      : `${SITE_URL}${image.startsWith("/") ? image : `/${image}`}`
    : `${SITE_URL}/og-default.png`;

  // Collect schemas
  const out = [];
  if (schema) out.push(schema);
  if (Array.isArray(schemas)) out.push(...schemas);

  if (breadcrumb && breadcrumb.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: breadcrumb.map((b, i) => ({
        "@type": "ListItem",
        position: i + 1,
        name: b.name,
        item: b.path?.startsWith("http") ? b.path : `${SITE_URL}${b.path}`,
      })),
    });
  }

  if (faq && faq.length) {
    out.push({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faq.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
  }

  if (article) {
    out.push({
      "@context": "https://schema.org",
      "@type": "Article",
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      headline: title,
      description: desc,
      image: ogImage,
      datePublished: article.publishedAt,
      dateModified: article.modifiedAt || article.publishedAt,
      author: { "@type": "Person", name: article.author || "GC SPARK Editorial" },
      publisher: {
        "@type": "Organization",
        name: BRAND.name,
        logo: { "@type": "ImageObject", url: `${SITE_URL}/favicon-256.png` },
      },
      articleSection: article.section,
      keywords: article.tags?.join(", "),
    });
  }

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {url && <link rel="canonical" href={url} />}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      {url && <meta property="og:url" content={url} />}
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={BRAND.name} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={ogImage} />
      {out.map((s, i) => (
        <script key={i} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
